import { StoredPersonWeek, StoredPersonWeekCta, StoredPersonWeekDay, StoredPersonWeekSignal } from '../models';

/**
 * No night-job flag marks meetings as high.
 * High means meeting time is at least half of tracked time already on the card.
 */
const MEETING_SHARE_NUMERATOR = 2;

const COMPARE_WEEK_PATH: Record<string, string> = {
  idle_time: '/activity-summary',
  manual_time: '/activity-summary',
  mobile_time: '/activity-summary',
  heavy_hours: '/hours-tracked',
  unusual_activity: '/unusual-activity',
  attendance_issues: '/timeline',
};

const PAGE_SIGNAL: Record<string, string> = {
  'unusual-activity': 'unusual_activity',
  'edit-time': 'manual_time',
  'hours-tracked': 'heavy_hours',
  'activity-summary': 'idle_time',
  timeline: 'attendance_issues',
};

export interface SecondaryHighlight {
  key: 'CHECK_MEETINGS' | 'COMPARE_TO_TEAM' | 'COMPARE_WEEKS';
  label: string;
  href: string;
}

type Candidate = {
  key: SecondaryHighlight['key'];
  label: string;
  path: string;
  from: string;
  to: string;
};

function finite(value: number | undefined): number {
  return typeof value === 'number' && Number.isFinite(value) && value > 0 ? value : 0;
}

function meetingsHigh(days: readonly StoredPersonWeekDay[]): boolean {
  let meeting = 0;
  let tracked = 0;
  for (const day of days) {
    meeting += finite(day.metrics?.meetingSec);
    tracked += finite(day.metrics?.trackedSec);
  }
  return tracked > 0 && meeting * MEETING_SHARE_NUMERATOR >= tracked;
}

function shiftIso(iso: string, days: number): string {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  if (!match) return '';
  const date = new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3])));
  date.setUTCDate(date.getUTCDate() + days);
  return date.toISOString().slice(0, 10);
}

function weekBounds(weekStart: string | undefined, weekEnd: string | undefined): { from: string, to: string } {
  const from = (weekStart ?? '').slice(0, 10);
  const end = (weekEnd ?? '').slice(0, 10);
  return { from, to: end || (from ? shiftIso(from, 6) : '') };
}

function fourWeekBounds(weekStart: string | undefined, weekEnd: string | undefined): { from: string, to: string } {
  const week = weekBounds(weekStart, weekEnd);
  if (!week.from) return week;
  return { from: shiftIso(week.from, -21), to: week.to };
}

function reportPath(href: string | undefined, page: string | undefined): string {
  const saved = page?.trim().replace(/^\//, '');
  if (saved) return `/${saved}`;
  const raw = href?.trim() ?? '';
  if (!raw) return '';
  const path = raw.split(/[?;#]/)[0];
  return path.startsWith('/') ? path : '';
}

/** Same `;from=;to=;userIds=` shape as buildPersonCardSignalLink. */
function reportHref(path: string, userId: string | undefined, from: string, to: string): string {
  const parts = [path];
  if (from && to) parts.push(`from=${from}`, `to=${to}`);
  if (userId) parts.push(`userIds=${encodeURIComponent(userId)}`);
  return parts.join(';');
}

function fired(signals: readonly StoredPersonWeekSignal[], type: string): boolean {
  return signals.some(signal => signal.type === type);
}

function compareSignal(week: StoredPersonWeek): string | undefined {
  const first = week.signals[0]?.type;
  if (week.primaryCta?.key !== 'REVIEW_EVIDENCE') return first;
  const page = reportPath(week.primaryCta.href, week.primaryCta.page).slice(1);
  const wanted = PAGE_SIGNAL[page];
  if (wanted === 'attendance_issues') {
    if (fired(week.signals, 'attendance_issues')) return 'attendance_issues';
    if (fired(week.signals, 'mobile_time')) return 'mobile_time';
    return first;
  }
  if (wanted && fired(week.signals, wanted)) return wanted;
  return first;
}

function toButton(candidate: Candidate, userId: string | undefined): SecondaryHighlight {
  return {
    key: candidate.key,
    label: candidate.label,
    href: reportHref(candidate.path, userId, candidate.from, candidate.to),
  };
}

/**
 * Outlined highlight. Always shown on an analyzed card.
 * The model does not pick it. Rate apps, project context, and screencasts
 * are left out: those facts are not on the card list.
 * Skip a match whose report equals the primary, then take the next one.
 * Compare weeks is still returned when it is the only option left.
 */
export function chooseSecondaryHighlight(week: StoredPersonWeek): SecondaryHighlight {
  const primary: StoredPersonWeekCta | undefined = week.primaryCta;
  const primaryPath = reportPath(primary?.href, primary?.page);
  const weekRange = weekBounds(week.weekStart, week.weekEnd);
  const span = fourWeekBounds(week.weekStart, week.weekEnd);
  const signal = compareSignal(week);
  const days = week.days ?? [];
  const signals = week.signals ?? [];
  const candidates: Candidate[] = [];
  if (fired(signals, 'idle_time') && meetingsHigh(days)) {
    candidates.push({
      key: 'CHECK_MEETINGS',
      label: 'CHECK MEETINGS',
      path: '/timeline',
      from: weekRange.from,
      to: weekRange.to,
    });
  }
  candidates.push({
    key: 'COMPARE_TO_TEAM',
    label: 'COMPARE TO TEAM',
    path: '/activity-summary',
    from: weekRange.from,
    to: weekRange.to,
  });
  candidates.push({
    key: 'COMPARE_WEEKS',
    label: 'COMPARE WEEKS',
    path: (signal && COMPARE_WEEK_PATH[signal]) || '/activity-summary',
    from: span.from,
    to: span.to,
  });
  for (let index = 0; index < candidates.length; index += 1) {
    const candidate = candidates[index];
    const last = index === candidates.length - 1;
    if (last || candidate.path !== primaryPath) return toButton(candidate, week.userId);
  }
  return toButton(candidates[candidates.length - 1], week.userId);
}
